import {
  CoveredPartyPayloadType,
  DriverType,
} from '@clearcover/core-app-types';
import * as Yup from 'yup';
import { format } from 'date-fns';
import {
  DriverOptionsResponse,
  DriverStatusType,
  ExcludedDriverOptionQuestionsConfigType,
} from './DriverForm';
import { camelize, currency, validateCoverageLimitWithK } from 'Shared/utils';
import { env } from 'Shared/constants/env';
import { convertFromToggleValue } from 'Policies/utils/helpers';
import { buildCoverage } from 'QuoteApp/services/endpoints/PostChallengePriorPolicy/request';
import {
  parseDateSlashFormatShortToISO,
  isServerDate,
  parseDateSlashFormatShort,
} from 'Shared/utils/date';

export const militaryStatusOptions = {
  full_time_active_duty: 'full_time_active_duty',
  active_national_guard_or_reserve: 'active_national_guard_or_reserve',
  discharged_or_retired: 'discharged_or_retired',
  no_military_history: 'no_military_history',
  unverified_military_service: 'unverified_military_service',
};

const DRIVER_INFO_EXPOSURE_DETAILS_VALIDATION = (
  driver: DriverType | undefined,
  askLivesInHousehold: boolean,
  askHasAccessToVehicles: boolean,
  askHasOwnInsurancePolicy: boolean
) => {
  return {
    is: () =>
      env.driverNewQuestionsEnabled &&
      driver?.policyPartyRelationship !== 'named_insured',
    then: Yup.object({
      livesWithPolicyholder: askLivesInHousehold
        ? Yup.string().required('Required')
        : Yup.string().notRequired(),
      hasAccessToVehicles: askHasAccessToVehicles
        ? Yup.string().required('Required')
        : Yup.string().notRequired(),
      hasOwnAutoPolicy: askHasOwnInsurancePolicy
        ? Yup.string().required('Required')
        : Yup.string().notRequired(),
    }),
    otherwise: Yup.object().notRequired(),
  };
};

const EXPOSURE_DETAILS_VALIDATION = (
  driver: DriverType | undefined,
  askLivesInHousehold: boolean,
  askHasAccessToVehicles: boolean,
  askHasOwnInsurancePolicy: boolean
) => {
  return {
    exposureDetails: Yup.object().when(
      [],
      DRIVER_INFO_EXPOSURE_DETAILS_VALIDATION(
        driver,
        askLivesInHousehold,
        askHasAccessToVehicles,
        askHasOwnInsurancePolicy
      )
    ),
  };
};

const LICENSE_VALIDATION = (askForUserReportedLicenseStatus: boolean) => {
  return {
    licenseType: !askForUserReportedLicenseStatus
      ? Yup.string().required('Required')
      : Yup.string().notRequired(),
    licenseNumber: Yup.string().when(
      ['licenseType', 'licenseUserReportedStatus'],
      {
        is: (licenseType: string, licenseUserReportedStatus?: string) =>
          licenseType === 'learners' ||
          licenseType === 'foreign' ||
          isNonLicensed(licenseUserReportedStatus),
        then: Yup.string().notRequired(),
        otherwise: Yup.string().required('Required'),
      }
    ),
    licenseState: Yup.string().when(
      ['licenseType', 'licenseUserReportedStatus'],
      {
        is: (licenseType: string, licenseUserReportedStatus?: string) =>
          licenseType === 'learners' ||
          licenseType === 'foreign' ||
          isNonLicensed(licenseUserReportedStatus),
        then: Yup.string().notRequired(),
        otherwise: Yup.string().required('Required'),
      }
    ),
    licenseStatus: Yup.string().when('licenseType', {
      is: (licenseType: string) =>
        licenseType === 'learners' ||
        licenseType === 'foreign' ||
        askForUserReportedLicenseStatus,
      then: Yup.string().notRequired(),
      otherwise: Yup.string().required('Required'),
    }),
    licenseUserReportedStatus: askForUserReportedLicenseStatus
      ? Yup.string().nullable().required('Required')
      : Yup.string().notRequired(),
  };
};

/**
 * Creates the Yup validation schema for prior policy coverages.
 * Validates individual coverage formats and ensures at least one coverage is provided.
 * Exported for testing purposes.
 */
export const createPriorPolicyCoveragesSchema = () =>
  Yup.object({
    bodilyInjury: Yup.string()
      .nullable()
      .test(
        'bi-format',
        'Invalid format (e.g., 100K/300K)',
        (value) => validateCoverageLimitWithK(value || '', 'BI').isValid
      ),
    combinedSingleLimit: Yup.string()
      .nullable()
      .test(
        'csl-format',
        'Invalid format (e.g., 100K)',
        (value) => validateCoverageLimitWithK(value || '', 'CSL').isValid
      ),
    personalInjuryProtection: Yup.string()
      .nullable()
      .test(
        'pip-format',
        'Invalid format (e.g., 10K)',
        (value) => validateCoverageLimitWithK(value || '', 'PIP').isValid
      ),
    // Phantom field for cross-field validation: ensures at least one coverage limit is provided
    limits: Yup.string().when(
      ['bodilyInjury', 'combinedSingleLimit', 'personalInjuryProtection'],
      {
        is: (
          bodilyInjury: string,
          combinedSingleLimit: string,
          personalInjuryProtection: string
        ) => !bodilyInjury && !combinedSingleLimit && !personalInjuryProtection,
        then: Yup.string().required('One is required'),
        otherwise: Yup.string(),
      }
    ),
  });

export function createDriverYupValidation(
  type: string,
  driver: DriverType | undefined,
  details: {
    region: string;
    effectiveDate: string;
    timeZone: string | undefined;
    filingIdentifier: string;
    isPolicyDraft: boolean;
    isRenewalDraft: boolean;
    termId: string;
  },
  {
    military,
    student,
    driverOptionsQuestionsConfig: {
      askStateResidencyDetails,
      askHasAccessToVehicles,
      askHasOwnInsurancePolicy,
      askLivesInHousehold,
      askForUserReportedLicenseStatus,
      askForOccupation = true,
      askForEducation = true,
    },
    excludedDriverOptionsQuestionsConfig,
  }: DriverOptionsResponse,
  driverStatus: DriverStatusType,
  canChallengePriorPolicy: boolean = false
) {
  const DRIVER_INFO_VALIDATION = {
    is: (val: string) => val !== 'excluded_person',
    then: Yup.object({
      firstName: Yup.string().required('Required'),
      middleInitial: Yup.string(),
      lastName: Yup.string().required('Required'),
      suffix: Yup.string(),
      dateOfBirth: Yup.string().required('Required'),
      gender: Yup.string().required('Required'),
      maritalStatus: Yup.string().required('Required'),
      relationToApplicant: Yup.string().required('Required'),
      ...EXPOSURE_DETAILS_VALIDATION(
        driver,
        askLivesInHousehold,
        askHasAccessToVehicles,
        askHasOwnInsurancePolicy
      ),
      ...LICENSE_VALIDATION(askForUserReportedLicenseStatus),
    }),
    otherwise: Yup.object({
      firstName: Yup.string().required('Required'),
      middleInitial: Yup.string(),
      lastName: Yup.string().required('Required'),
      dateOfBirth: Yup.string().required('Required'),
      relationToApplicant: Yup.string().required('Required'),
      ...(excludedDriverOptionsQuestionsConfig?.askForGender
        ? {
            gender: Yup.string().required('Required'),
          }
        : {}),
      ...(excludedDriverOptionsQuestionsConfig?.askForMaritalStatus
        ? {
            maritalStatus: Yup.string().required('Required'),
          }
        : {}),
    }),
  };

  const DRIVER_ATTRIBUTES_VALIDATION = {
    is: (val: string) =>
      val !== 'excluded_person' && driverStatus !== 'listed_person',
    then: Yup.object({
      educationLevel: Yup.string().when([], {
        is: () => askForEducation ?? true,
        then: Yup.string().required('Required'),
        otherwise: Yup.string(),
      }),
      occupation: Yup.string().when([], {
        is: () => askForOccupation ?? true,
        then: Yup.string().required('Required'),
        otherwise: Yup.string(),
      }),
      ageFirstLicensed: Yup.string()
        .matches(/^\d+$/, 'Should contain numbers only')
        .notRequired(),
      homeowner: Yup.string().required('Required'),
      inStateResidencyDuration: Yup.string().when([], {
        is: () => env.driverNewQuestionsEnabled && askStateResidencyDetails,
        then: Yup.string().required('Required'),
        otherwise: Yup.string(),
      }),
      isStateResidentWithOutOfStateHomes: Yup.string().when(
        'inStateResidencyDuration',
        {
          is: (val: string) =>
            val === 'lessThan10Months' &&
            env.driverNewQuestionsEnabled &&
            askStateResidencyDetails,
          then: Yup.string().required('Required'),
          otherwise: Yup.string(),
        }
      ),
      isOutOfStateStudent: Yup.string().when('inStateResidencyDuration', {
        is: (val: string) =>
          val === 'lessThan10Months' &&
          env.driverNewQuestionsEnabled &&
          askStateResidencyDetails,
        then: Yup.string().required('Required'),
        otherwise: Yup.string(),
      }),
      hasVehicleWhileOutOfStateStudent: Yup.string().when(
        'inStateResidencyDuration',
        {
          is: (val: string) =>
            val === 'lessThan10Months' &&
            env.driverNewQuestionsEnabled &&
            askStateResidencyDetails,
          then: Yup.string().required('Required'),
          otherwise: Yup.string(),
        }
      ),
    }),
    otherwise: Yup.object().notRequired(),
  };

  const DRIVER_REPORTS_VALIDATION = {
    is: (val: string) => val !== 'excluded_person' && type === 'update',
    then: Yup.object({
      sr22s: Yup.object({
        ...driver?.sr22s.reduce(
          (acc, sr22) => ({
            ...acc,
            [sr22.id]: Yup.object({
              challenge: Yup.string().notRequired(),
              startDate: Yup.string().required('Required'),
              endDate: Yup.string().notRequired(),
            }),
          }),
          {}
        ),
      }),
      accidents: Yup.object({
        ...driver?.accidents.reduce(
          (acc, accident) => ({
            ...acc,
            [accident.id]: Yup.object({
              challenge: Yup.string().notRequired(),
              accidentDate: Yup.string().required('Required'),
              isAtFault: Yup.string().required('Required'),
              damageAmount: Yup.string().notRequired(),
            }),
          }),
          {}
        ),
      }),
      violations: Yup.object({
        ...driver?.violations.reduce(
          (acc, violation) => ({
            ...acc,
            [violation.id]: Yup.object({
              challenge: Yup.string().notRequired(),
              violationDate: Yup.string().required('Required'),
              convictionDate: Yup.string().notRequired(),
              description: Yup.string().required('Required'),
            }),
          }),
          {}
        ),
      }),
      driversLicense: Yup.object({
        challenge: Yup.string().notRequired(),
        ...(askForUserReportedLicenseStatus
          ? {}
          : {
              status: Yup.string().test(
                'Required license status',
                'Required',
                function (val) {
                  const { licenseType } = (this as any).from[2].value.info;
                  return (
                    licenseType === 'learners' ||
                    licenseType === 'foreign' ||
                    !!val
                  );
                }
              ),
            }),
      }),

      priorCarrier: canChallengePriorPolicy
        ? Yup.object().notRequired()
        : Yup.object({
            challenge: Yup.string().notRequired(),
            carrier: Yup.string().notRequired(),
            isCurrentlyInsured: Yup.boolean().notRequired(),
            bodyInjuryLimits: Yup.string().notRequired(),
            duration: Yup.object({
              currentlyInsuredMonths: Yup.string()
                .matches(/^\d+$/, 'Should contain numbers only')
                .notRequired(),
              daysSinceLastInsured: Yup.string()
                .matches(/^\d+$/, 'Should contain numbers only')
                .notRequired(),
            }),
          }),
      priorPolicies: canChallengePriorPolicy
        ? Yup.array().of(
            Yup.object({
              challenge: Yup.string().notRequired(),
              source: Yup.string().notRequired(),
              currentCarrierName: Yup.string().required('Required'),
              policyNumber: Yup.string().required('Required'),
              startDate: Yup.string().required('Required'),
              endDate: Yup.string()
                .required('Required')
                .test(
                  'endDate-after-startDate',
                  'Must be after Effective date',
                  function (value) {
                    const { startDate } = this.parent;
                    if (!value || !startDate) return true;

                    const parseDate = (dateStr: string): Date | null => {
                      if (isServerDate(dateStr)) {
                        return new Date(dateStr);
                      }
                      if (dateStr.includes('/')) {
                        return parseDateSlashFormatShort(dateStr);
                      }
                      try {
                        return new Date(dateStr);
                      } catch {
                        return null;
                      }
                    };

                    try {
                      const startDateObj = parseDate(startDate);
                      const endDateObj = parseDate(value);
                      if (!startDateObj || !endDateObj) {
                        return true;
                      }
                      return endDateObj >= startDateObj;
                    } catch {
                      return true;
                    }
                  }
                ),
              amBestNumber: Yup.string()
                .matches(/^\d*$/, 'Should contain numbers only')
                .test(
                  'max-length',
                  'Maximum 10 digits',
                  (value) => !value || value.length <= 10
                )
                .nullable()
                .notRequired(),
              state: Yup.string().required('Required'),
              coverages: createPriorPolicyCoveragesSchema(),
            })
          )
        : Yup.array().notRequired(),
    }),
    otherwise: Yup.object().notRequired(),
  };

  const DRIVER_VEHICLE_ASSIGNMENTS_VALIDATION = {
    is: (val: string) =>
      val !== 'excluded_person' && details.region === 'US-VA',
    then: Yup.array()
      .ensure()
      .of(Yup.string().required())
      .compact()
      .min(1, 'Each driver must be assigned to at least one vehicle')
      .required('Required'),
    otherwise: Yup.array().notRequired(),
  };
  const DRIVER_MILITARY_VALIDATION = {
    is: (val: string) =>
      val !== 'excluded_person' &&
      driverStatus !== 'listed_person' &&
      (military.enabled ?? true),
    then: Yup.object({
      militaryStatus: Yup.string().required('Required'),
      rank: Yup.string().when('militaryStatus', {
        is: militaryStatusOptions.no_military_history,
        then: Yup.string().notRequired(),
        otherwise: Yup.string().required('Required'),
      }),
      isDeployed: Yup.string().when('militaryStatus', {
        is: (status: string) =>
          status === militaryStatusOptions.discharged_or_retired ||
          status === militaryStatusOptions.no_military_history,
        then: Yup.string().notRequired(),
        otherwise: Yup.string().required('Required'),
      }),
      isCertifiedMilitaryDependent: Yup.string().when('militaryStatus', {
        is: () => military.allowDependentDiscount,
        then: Yup.string().required('Required'),
        otherwise: Yup.string().notRequired(),
      }),
    }),
  };

  const DRIVER_VALIDATION = Yup.object({
    policyPartyRelationship: Yup.string(),
    info: Yup.object().when('policyPartyRelationship', DRIVER_INFO_VALIDATION),
    attributes: Yup.object().when(
      'policyPartyRelationship',
      DRIVER_ATTRIBUTES_VALIDATION
    ),
    assignedVehicles: Yup.array().when(
      'policyPartyRelationship',
      DRIVER_VEHICLE_ASSIGNMENTS_VALIDATION
    ),
    reports: Yup.object().when(
      'policyPartyRelationship',
      DRIVER_REPORTS_VALIDATION
    ),
    military: Yup.object().when(
      'policyPartyRelationship',
      DRIVER_MILITARY_VALIDATION
    ),
    cfrs: Yup.array().of(
      Yup.object({
        required: Yup.boolean(),
        type: Yup.string().when('required', {
          is: true,
          then: Yup.string().required('Required'),
        }),
        caseNumber: Yup.string().when('required', {
          is: true,
          then: Yup.string().required('Required'),
        }),
        state: Yup.string().when('required', {
          is: true,
          then: Yup.string().required('Required'),
        }),
      })
    ),
    student: Yup.object().when('policyPartyRelationship', {
      is: (val) =>
        student?.allowGoodStudentDiscount && val !== 'excluded_person',
      then: Yup.object({
        goodStudentStatus: Yup.string().required('Required'),
      }),
      otherwise: Yup.object().notRequired(),
    }),
  });
  return DRIVER_VALIDATION;
}

export function makeCreateUpdateDriverPayload({
  values,
  type,
  requiredAccessStatements,
  excludedDriverOptionsQuestionsConfig,
  bypassUnderwriting,
}: {
  values: CoveredPartyPayloadType;
  type: string;
  requiredAccessStatements: boolean;
  excludedDriverOptionsQuestionsConfig: ExcludedDriverOptionQuestionsConfigType;
  bypassUnderwriting?: boolean;
}) {
  const commonInfo = {
    policyPartyRelationship: values.policyPartyRelationship,
    firstName: values.info.firstName,
    middleInitial: values.info.middleInitial,
    lastName: values.info.lastName,
    suffix: values.info.suffix,
    relationToApplicant: values.info.relationToApplicant,
    dateOfBirth: format(Date.parse(values.info.dateOfBirth), 'yyyy-MM-dd'),
  };

  const commonInfoLicense = {
    licenseState: `US-${values.info.licenseState}`,
    licenseStatus: values.info.licenseStatus?.toLowerCase(),
  };

  const commonInfoExposureDetails = {
    livesWithPolicyholder: convertFromToggleValue(
      values.info.exposureDetails.livesWithPolicyholder
    ),
    hasAccessToVehicles: convertFromToggleValue(
      values.info.exposureDetails.hasAccessToVehicles
    ),
    hasOwnAutoPolicy: convertFromToggleValue(
      values.info.exposureDetails.hasOwnAutoPolicy
    ),
  };

  const commonAttributes = {
    ...values.attributes,
    military: !values.military
      ? undefined
      : {
          ...values.military,
          enlisted: values.military.enlisted === 'true',
          isDeployed: values.military.isDeployed === 'true',
        },
    homeowner: values.attributes.homeowner === 'true',
    isStateResidentWithOutOfStateHomes:
      values.attributes.isStateResidentWithOutOfStateHomes === 'true',
    isOutOfStateStudent: values.attributes.isOutOfStateStudent === 'true',
    hasVehicleWhileOutOfStateStudent:
      values.attributes.hasVehicleWhileOutOfStateStudent === 'true',
  };

  const commonMilitary = !values.military
    ? undefined
    : {
        militaryStatus: values.military.militaryStatus,
        isDeployed: values.military.isDeployed === 'true',
        rank: values.military.rank,
        isCertifiedMilitaryDependent:
          values.military.isCertifiedMilitaryDependent === 'true',
      };

  const accessStatementAccepted = requiredAccessStatements
    ? {
        accessDriverStatementAccepted: true,
      }
    : {};

  const commonCFRS = values?.cfrs?.map(
    (cfr: CoveredPartyPayloadType['cfrs']) => ({
      ...cfr,
      type: cfr.required ? cfr.type : undefined,
      caseNumber: cfr.required ? cfr.caseNumber : undefined,
      state: cfr.required && cfr.state ? `US-${cfr.state}` : undefined,
      required: cfr.required,
    })
  );

  let payload;

  switch (values.policyPartyRelationship) {
    case 'excluded_person':
      payload = {
        type,
        identifier: values.identifier,
        info: {
          ...commonInfo,
          ...(excludedDriverOptionsQuestionsConfig?.askForGender
            ? { gender: values.info.gender }
            : {}),
          ...(excludedDriverOptionsQuestionsConfig?.askForMaritalStatus
            ? { maritalStatus: values.info.maritalStatus }
            : {}),
        },
      };
      break;
    case 'listed_person':
      payload = {
        type,
        identifier: values.identifier,
        info: {
          ...values.info,
          ...commonInfo,
          ...commonInfoLicense,
          ...commonInfoExposureDetails,
          ...accessStatementAccepted,
        },
      };
      break;
    case 'named_insured':
    case 'covered_person':
    default:
      payload = {
        type,
        identifier: values.identifier,
        info: {
          ...values.info,
          ...commonInfo,
          ...commonInfoLicense,
          ...commonInfoExposureDetails,
          ...accessStatementAccepted,
        },
        attributes: commonAttributes,
        student: {
          goodStudentStatus: values.student.goodStudentStatus,
        },
        assignedVehicles: values.assignedVehicles,
        reports:
          type === 'update'
            ? {
                ...values.reports,
                priorPolicies: values.reports.priorPolicies?.map(
                  (
                    priorPolicy: CoveredPartyPayloadType['reports']['priorPolicies']
                  ) => {
                    return {
                      ...priorPolicy,
                      startDate: parseDateSlashFormatShortToISO(
                        priorPolicy.startDate
                      ),
                      endDate: parseDateSlashFormatShortToISO(
                        priorPolicy.endDate
                      ),
                      state: !priorPolicy.state.includes('-')
                        ? `US-${priorPolicy.state}`
                        : priorPolicy.state,
                      source: {
                        kind: camelize(priorPolicy.source),
                      },
                      coverages: [
                        buildCoverage(
                          'bodilyInjury',
                          priorPolicy.coverages.bodilyInjury
                        ),
                        buildCoverage(
                          'combinedSingleLimit',
                          priorPolicy.coverages.combinedSingleLimit
                        ),
                        buildCoverage(
                          'personalInjuryProtection',
                          priorPolicy.coverages.personalInjuryProtection
                        ),
                      ],
                    };
                  }
                ),
                accidents: Object.keys(values.reports.accidents).reduce(
                  (
                    acc: CoveredPartyPayloadType['reports']['accidents'],
                    accidentId: string
                  ) => ({
                    ...acc,
                    [accidentId]: {
                      challenge: values.reports.accidents[accidentId].challenge,
                      accidentDate:
                        values.reports.accidents[accidentId].accidentDate,
                      isAtFault: values.reports.accidents[accidentId].isAtFault,
                      damageAmount: currency(
                        values.reports.accidents[accidentId].damageAmount
                      ).toString(),
                      classifications:
                        values.reports.accidents[accidentId].classifications,
                    },
                  }),
                  {}
                ),
              }
            : undefined,
        military: commonMilitary,
        cfrs: commonCFRS,
      };
      break;
  }

  if (
    values.policyPartyRelationship !== 'excluded_person' &&
    (values.info.licenseType === 'learners' ||
      values.info.licenseType === 'foreign')
  ) {
    payload.info.licenseNumber = '';
    payload.info.licenseState = '';
    delete payload.info.licenseStatus;
  }

  if (isNonLicensed(values.info?.licenseUserReportedStatus ?? '')) {
    delete payload.info.licenseNumber;
    delete payload.info.licenseState;
  }

  return {
    ...payload,
    ...(bypassUnderwriting ? { bypassUnderwriting: true } : {}),
  };
}

export function createDriverStatusYupValidation(driverOptionsQuestionsConfig: {
  askLivesInHousehold: boolean;
  askHasAccessToVehicles: boolean;
  askHasOwnInsurancePolicy: boolean;
  askForUserReportedLicenseStatus: boolean;
}) {
  const DRIVER_INFO_VALIDATION = {
    is: (val: string) => val !== 'excluded_person',
    then: Yup.object({
      ...EXPOSURE_DETAILS_VALIDATION(
        undefined,
        driverOptionsQuestionsConfig.askLivesInHousehold,
        driverOptionsQuestionsConfig.askHasAccessToVehicles,
        driverOptionsQuestionsConfig.askHasOwnInsurancePolicy
      ),
      ...LICENSE_VALIDATION(
        driverOptionsQuestionsConfig.askForUserReportedLicenseStatus
      ),
    }),
  };

  const DRIVER_STATUS_VALIDATION = Yup.object({
    policyPartyRelationship: Yup.string(),
    info: Yup.object().when('policyPartyRelationship', DRIVER_INFO_VALIDATION),
  });
  return DRIVER_STATUS_VALIDATION;
}

export function makeCalculateDriverStatusPayload(
  values: CoveredPartyPayloadType
) {
  const payload: {
    exposureDetails: {
      livesWithPolicyholder: boolean | undefined;
      hasAccessToVehicles: boolean | undefined;
      hasOwnAutoPolicy: boolean | undefined;
    };
    isPolicyHolder: boolean;
    partialLicense?: {
      type: string;
      verified: boolean;
    };
    license?: {
      number: string;
      status: string;
      geographicLocation: string;
      source: {
        kind: string;
      };
    };
  } = {
    exposureDetails: {
      livesWithPolicyholder: convertFromToggleValue(
        values.info.exposureDetails.livesWithPolicyholder
      ),
      hasAccessToVehicles: convertFromToggleValue(
        values.info.exposureDetails.hasAccessToVehicles
      ),
      hasOwnAutoPolicy: convertFromToggleValue(
        values.info.exposureDetails.hasOwnAutoPolicy
      ),
    },
    isPolicyHolder: values.policyPartyRelationship === 'policy_holder',
  };

  if (values.info.licenseType === 'us_license') {
    payload.license = {
      number: values.info.licenseNumber,
      status: values.info.licenseStatus,
      geographicLocation: values.info.licenseState,
      source: {
        kind: 'clearcover',
      },
    };
  }

  if (
    values.info.licenseType === 'learners' ||
    values.info.licenseType === 'foreign'
  ) {
    payload.partialLicense = {
      type: values.info.licenseType,
      verified: true,
    };
  }

  return payload;
}

export const isNonLicensed = (licenseUserReportedStatus?: null | string) => {
  if (!licenseUserReportedStatus) return false;

  return (
    licenseUserReportedStatus === 'neverBeenLicensed' ||
    licenseUserReportedStatus === 'international'
  );
};
